import { useEffect, useRef, useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { DataService } from "../services/DataService";
import { UserService } from "../services/UserService";
import { OrganisationService } from "../services/OrganisationService";
import { logger } from "../utils/logger";

const REFRESH_INTERVAL = 120000;

export const useDataManagement = (
  user,
  selectedOrganisation,
  isShare,
  getAccessTokenSilently,
  isAuthenticated,
  isAuthLoading
) => {
  const interval = useRef(null);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const initializationRef = useRef({ completed: false, userId: null });

  // Query for organization data - wait for Auth0 authentication AND initialization to complete
  const { data: organizationData, isLoading } = useQuery({
    queryKey: ["organizationData", user?.email],
    queryFn: () => DataService.refreshData(user.email, user.sub),
    enabled:
      isAuthenticated &&
      !isAuthLoading &&
      !!user?.email &&
      !!user?.sub &&
      isInitialized,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Update Redux when organization data changes
  const updateOrganizationData = useCallback(
    (data) => {
      if (data?.selectedOrganisation) {
        OrganisationService.setSelectedOrganisation(data.selectedOrganisation);

        // Also update organizations in Redux if needed
        if (data?.organisations) {
          dispatch({
            type: "organisations/update",
            payload: data.organisations,
          });
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (organizationData) {
      updateOrganizationData(organizationData);
    }
  }, [organizationData, updateOrganizationData]);

  // Setup refresh interval - only depend on user existence, not selectedOrganisation
  useEffect(() => {
    if (!user?.email) return;

    interval.current = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["organizationData"] });
    }, REFRESH_INTERVAL);

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [user?.email, queryClient]); // Removed selectedOrganisation to prevent constant recreation

  // Initialize user data - prevent multiple initializations for same user
  useEffect(() => {
    // Wait for Auth0 to finish loading and authenticate before initializing
    if (
      isAuthLoading ||
      !isAuthenticated ||
      !user?.sub ||
      initializationRef.current.userId === user.sub
    )
      return;

    const initializeData = async () => {
      try {
        initializationRef.current = { completed: false, userId: user.sub };

        // Ensure we have a valid token before making any API calls
        await getAccessTokenSilently();

        // First-login users need a DB row before any guarded API endpoints can work.
        await UserService.fetchUser(user.email, user.sub);

        if (!isShare) {
          await UserService.checkBilling();
        }

        initializationRef.current.completed = true;
        setIsInitialized(true); // Enable organization data query
      } catch (error) {
        logger.error("Failed to initialize user data:", error);
        // API refusal on first login, or the Auth0 Action denying silent renewal
        if (
          error?.response?.data?.detail?.code === "email_not_verified" ||
          error?.error_description === "email_not_verified"
        ) {
          setEmailNotVerified(true);
        }
        initializationRef.current = { completed: false, userId: null };
        setIsInitialized(false);
      }
    };

    initializeData();
  }, [
    user?.sub,
    user?.email,
    isShare,
    getAccessTokenSilently,
    isAuthenticated,
    isAuthLoading,
  ]);

  return {
    organizationData,
    loading: isLoading,
    emailNotVerified,
  };
};
